/**
 * Water polo sport pack — pure declarative data.
 *
 * Nothing in `core` or `renderer` may reference anything in this file. If a
 * water-polo fact is needed somewhere else, the fix is to widen the pack
 * contract, not to import from here.
 *
 * Geometry is normative and documented in `docs/rules/water-polo-field.md`.
 * Read it before changing any number here; several of these are counterintuitive
 * and the doc lists the specific misreadings to avoid.
 *
 * Field of play (World Aquatics WP 1): 25 m between goal lines, 20 m wide.
 * Distinctive marks measured from the goal line: 2 m (red), 5 m (red, the
 * penalty throw point), 6 m (yellow, the penalty distance since the 2019 rule
 * change), halfway (white). The lane rope carries the same colour coding: red
 * from the goal line to 2 m, yellow to 6 m, green from there to halfway. The
 * 5 m mark sits inside the yellow band and does not break it.
 *
 * The 2 m restriction is a goal-mouth box (WP 1.2, WP 21.10), not a line across
 * the pool and not the goal mouth either: it runs from the goal line out to 2 m
 * and reaches 2 m beyond the lateral outside of each post, so 2.00 + 3.16 + 2.00
 * = 7.16 m wide. If its edges line up with the posts, it is wrong. The zone is
 * notional — nothing marks it in the water — so it is drawn dashed. The 2 m
 * distance itself stays marked on both lane ropes, where it measures distance
 * rather than the restricted zone.
 *
 * The excluded players' re-entry area is a 2.00 m x 1.08 m rectangle in the two
 * corners on the side opposite the officials' table. The 2.00 m lies along the
 * goal line from the corner; the 1.08 m runs back behind it.
 *
 * It, the goal, and everything in `venueMarkings` fall outside the field
 * rectangle. `surface.surround` declares how far that is allowed to go; the two
 * are separate properties, because the goal is outside the field and must always
 * be drawn, while a referee platform is outside the field and defaults to off.
 *
 * Field space: x runs along the 25 m length, y across the 20 m width. A metre is
 * a different normalised length on each axis, so `lx` and `ly` are not
 * interchangeable — 2 m of box depth is 0.08 in x, 2 m of post clearance is 0.10
 * in y.
 */

import type { FormationSlot, SportPack } from '../../core/index.ts';

const LENGTH_M = 25;
const WIDTH_M = 20;

/** Metres along the length, as a 0..1 coordinate. */
const lx = (metres: number): number => metres / LENGTH_M;
/** Metres across the width, as a 0..1 coordinate. */
const ly = (metres: number): number => metres / WIDTH_M;

const TWO_M = lx(2);
const FIVE_M = lx(5);
const SIX_M = lx(6);
const HALF = 0.5;

/** Goal, in metres: a 3.00 m clear opening framed by a 0.08 m post each side. */
const GOAL_OPENING_WIDTH_M = 3.0;
const GOAL_POST_M = 0.08;
const GOAL_OUTER_WIDTH_M = GOAL_OPENING_WIDTH_M + 2 * GOAL_POST_M; // 3.16
/** The goal sits in a recess between the pool wall and the goal line. */
const GOAL_RECESS_M = 0.3;
/** The 2 m box reaches this far beyond the lateral outside of each post. */
const GOAL_AREA_CLEARANCE_M = 2.0;
/** Excluded players' re-entry rectangle (Competition Regulations 18.3.4). */
const RE_ENTRY_ALONG_GOAL_LINE_M = 2.0;
const RE_ENTRY_DEPTH_M = 1.08;

/**
 * Venue surround, in metres beyond each edge of the field of play.
 *
 * Lateral (3.20 m) is sourced: the field-of-play diagram's edge chain is
 * 1.00 m clear water + 1.20 m deck + 1.00 m referee platform, and it reads the
 * same at both sidelines.
 *
 * End (2.00 m) is a *presentation choice, not a rule* — no source dimensions the
 * space behind a goal line. It covers the 1.08 m re-entry area and the 0.30 m
 * goal recess and leaves room for the schematic end markers. Do not cite it as
 * a regulation; see `docs/rules/water-polo-field.md` section 6a.
 */
const SURROUND_LATERAL_M = 3.2;
const SURROUND_END_M = 2.0;

/** Referee platform (18.3.2): 1 m wide, colour coded to the lane ropes. */
const PLATFORM_WIDTH_M = 1.0;
/** Its inner edge, measured out from the sideline: past the 1 m of clear water and the deck. */
const PLATFORM_INNER_M = 2.2;
/** Flying substitution area (18.2.7): at least 0.50 m wide. Drawn at 1.00 m. */
const SUB_AREA_WIDTH_M = 1.0;

/**
 * Team benches (18.2.7). The rule places them on the side opposite the official
 * table and says the substitution area runs "between the goal line in front of
 * the team bench and the centre of the field of play" — so *which half each
 * bench occupies is sourced*: a bench sits at its own team's end, on the bench
 * side, in front of the goal line its substitution segment starts from.
 *
 * The three numbers below are **not** sourced. Nothing in the regulations or the
 * diagram dimensions a bench. They are a presentation choice on the same footing
 * as the officials' equipment: schematic markers that read as benches at board
 * scale. Do not cite them, and do not treat changing them as a geometry change.
 */
const BENCH_LENGTH_M = 6.0;
const BENCH_DEPTH_M = 0.9;
/** Offset from the bench's own goal line, so it reads as "in front of" it. */
const BENCH_INSET_M = 0.5;
/** Benches stand on the deck band, clear of the substitution water and the platform. */
const BENCH_OUT_M = 1.2;

const GOAL_HALF_SPAN = ly(GOAL_OUTER_WIDTH_M / 2);
const GOAL_AREA_HALF_SPAN = ly(GOAL_OUTER_WIDTH_M / 2 + GOAL_AREA_CLEARANCE_M);
const GOAL_DEPTH = lx(GOAL_RECESS_M);
const RE_ENTRY_SPAN = ly(RE_ENTRY_ALONG_GOAL_LINE_M);
const RE_ENTRY_DEPTH = lx(RE_ENTRY_DEPTH_M);

/**
 * Which sideline the officials' table sits on. The re-entry areas and the flying
 * substitution area are all on the *opposite* side, so one constant places them
 * all. The rules fix their side relative to the table but not the table's own
 * side, so this is our convention: it matches the orientation of the World
 * Aquatics field-of-play diagram, which puts the table at the top.
 */
const TABLE_EDGE: 0 | 1 = 0;
const FAR_EDGE = TABLE_EDGE === 0 ? 1 : 0;

const LINE = 0.0045;
const ROPE = 0.011;
/** How far a distance tick reaches in from the lane rope. */
const EDGE_MARK = ly(0.5);

const COLORS = {
  water: '#0d5a8f',
  white: '#f2f6fa',
  red: '#e0323c',
  yellow: '#f2c230',
  green: '#3fae63',
  goal: '#ffffff',
  label: 'rgba(255,255,255,0.62)',
  /** Venue detail sits on the dark backdrop, so it is drawn as pale outlines. */
  venue: 'rgba(255,255,255,0.5)',
  venueFill: 'rgba(255,255,255,0.08)',
  venueLabel: 'rgba(255,255,255,0.42)',
  subArea: 'rgba(242,194,48,0.55)',
} as const;

/**
 * A point `metres` outside the given sideline. `edge` is a y coordinate of the
 * field rectangle, so 0 counts outward as negative and 1 as positive.
 */
const outward = (edge: 0 | 1, metres: number): number => (edge === 0 ? -ly(metres) : 1 + ly(metres));

/**
 * Colour-coded lane rope along one long edge.
 *
 * Also used for the referee platforms, which 18.3.2 requires to carry the same
 * coding as the rope they parallel — same bands, different y and thickness.
 */
function ropeSegments(y: number, idPrefix: string, width: number = ROPE) {
  const bands: { from: number; to: number; color: string }[] = [
    { from: 0, to: TWO_M, color: COLORS.red },
    { from: TWO_M, to: SIX_M, color: COLORS.yellow },
    { from: SIX_M, to: HALF, color: COLORS.green },
    { from: HALF, to: 1 - SIX_M, color: COLORS.green },
    { from: 1 - SIX_M, to: 1 - TWO_M, color: COLORS.yellow },
    { from: 1 - TWO_M, to: 1, color: COLORS.red },
  ];
  return bands.map((band, index) => ({
    kind: 'line' as const,
    id: `${idPrefix}-${index}`,
    from: { x: band.from, y },
    to: { x: band.to, y },
    color: band.color,
    width,
  }));
}

function verticalLine(id: string, x: number, color: string, dash?: number[]) {
  return {
    kind: 'line' as const,
    id,
    from: { x, y: 0 },
    to: { x, y: 1 },
    color,
    width: LINE,
    ...(dash ? { dash } : {}),
  };
}

/** Dash and gap of the halfway line, in field units. */
const HALFWAY_DASH = [0.02, 0.016] as const;
/** Radius of the centre mark, in field units. */
const CENTRE_MARK_RADIUS = 0.008;

/**
 * One half of the halfway line, running from beside the centre mark out to
 * the pool edge.
 *
 * A single full-height dashed line would run straight through the mark: its
 * dash period has nothing to do with where the mark sits, and the mark is
 * stroke-only, so the dash inside would show. Each half instead stops one
 * dash gap short of the mark's edge, so the line reads dash, gap, mark, gap,
 * dash. Drawing from the mark outward puts the whole first dash beside the
 * mark and leaves any partial dash at the pool edge, where nothing is there to
 * notice it. The mark's radius is a fraction of the shorter side, which is the
 * side this line runs along, so it converts one to one.
 */
function halfwayLine(id: string, edge: 0 | 1) {
  const [, gap] = HALFWAY_DASH;
  const clearance = CENTRE_MARK_RADIUS + gap;
  return {
    kind: 'line' as const,
    id,
    from: { x: HALF, y: edge === 0 ? HALF - clearance : HALF + clearance },
    to: { x: HALF, y: edge },
    color: COLORS.white,
    width: LINE,
    dash: [...HALFWAY_DASH],
  };
}

/**
 * The 2 m zone (WP 1.2): 7.16 m wide by 2 m deep in front of the goal, inside
 * which an attacker without the line of the ball commits an ordinary foul.
 * Three sides are drawn — the goal line closes the fourth, so the boundary is
 * not stroked twice. Dashed, because the zone is notional: unlike the rope
 * marks, nothing corresponds to it in the water.
 */
function twoMetreBox(id: string, side: 'left' | 'right') {
  const goalLine = side === 'left' ? 0 : 1;
  const depth = side === 'left' ? TWO_M : 1 - TWO_M;
  return {
    kind: 'polyline' as const,
    id,
    points: [
      { x: goalLine, y: 0.5 - GOAL_AREA_HALF_SPAN },
      { x: depth, y: 0.5 - GOAL_AREA_HALF_SPAN },
      { x: depth, y: 0.5 + GOAL_AREA_HALF_SPAN },
      { x: goalLine, y: 0.5 + GOAL_AREA_HALF_SPAN },
    ],
    color: COLORS.red,
    width: LINE,
    dash: [0.014, 0.011],
  };
}

/** A distance tick on one lane rope, keeping the mark readable from the pool edge. */
function edgeMark(id: string, x: number, edge: 'top' | 'bottom', color: string) {
  const outer = edge === 'top' ? 0 : 1;
  const inner = edge === 'top' ? EDGE_MARK : 1 - EDGE_MARK;
  return {
    kind: 'line' as const,
    id,
    from: { x, y: outer },
    to: { x, y: inner },
    color,
    width: LINE,
  };
}

/**
 * The excluded players' re-entry area: a 2.00 m x 1.08 m rectangle in the corner
 * at each end, on the side opposite the officials' table.
 *
 * The 2.00 m is the side lying along the goal line, measured from the corner
 * inward (Competition Regulations 18.3.1); the 1.08 m is the perpendicular side
 * (18.3.4), which the field-of-play diagram places *behind* the goal line. So
 * this is the second marking, after the goal, that falls outside x in [0,1].
 *
 * Drawn as three sides, open along the sideline, matching the diagram.
 */
function reEntryArea(id: string, side: 'left' | 'right') {
  const goalLine = side === 'left' ? 0 : 1;
  const back = side === 'left' ? -RE_ENTRY_DEPTH : 1 + RE_ENTRY_DEPTH;
  const inner = FAR_EDGE === 1 ? 1 - RE_ENTRY_SPAN : RE_ENTRY_SPAN;
  return {
    kind: 'polyline' as const,
    id,
    points: [
      { x: goalLine, y: FAR_EDGE },
      { x: goalLine, y: inner },
      { x: back, y: inner },
      { x: back, y: FAR_EDGE },
    ],
    color: COLORS.red,
    width: LINE,
  };
}

/**
 * The goal frame, filling the 0.30 m recess between the pool wall and the goal
 * line. Drawn at its 3.16 m outer width; the 0.08 m posts are well under a pixel
 * at board scale, so the clear opening is not drawn separately.
 */
function goal(id: string, side: 'left' | 'right') {
  const x = side === 'left' ? -GOAL_DEPTH : 1;
  return {
    kind: 'rect' as const,
    id,
    at: { x, y: 0.5 - GOAL_HALF_SPAN },
    size: { w: GOAL_DEPTH, h: GOAL_HALF_SPAN * 2 },
    color: COLORS.goal,
    fill: 'rgba(255,255,255,0.14)',
    width: LINE * 1.4,
  };
}

/* ------------------------------------------------------------------ */
/* Venue detail — outside the field of play, drawn only on request      */
/* ------------------------------------------------------------------ */

/**
 * The referee platforms (Competition Regulations 18.3.2): 1 m wide, running the
 * length of the pool on both sides, "colour coded to the lane ropes". That last
 * clause is why this reuses `ropeSegments` rather than drawing a plain band —
 * the platform carries the same red / yellow / green coding as the rope beside
 * it, so deriving it from one helper keeps the two from drifting apart.
 *
 * Placed at the outer metre of the surround, 2.20 m to 3.20 m from the sideline,
 * so the line sits at its 2.70 m centre.
 */
function refereePlatform(edge: 0 | 1, idPrefix: string) {
  return ropeSegments(
    outward(edge, PLATFORM_INNER_M + PLATFORM_WIDTH_M / 2),
    idPrefix,
    ly(PLATFORM_WIDTH_M),
  );
}

/**
 * The flying substitution area (18.2.7): at least 0.50 m wide, running "between
 * the goal line in front of the team bench and the centre of the field of play".
 *
 * Two segments, not one band: the extent is stated per team, each running from
 * its own goal line to the centre, so a single line across the length would
 * misstate what the rule grants. It lies immediately outside the sideline
 * opposite the official table, on the bench side, in the metre of clear water.
 */
function substitutionArea(id: string, side: 'left' | 'right') {
  const y = outward(FAR_EDGE, SUB_AREA_WIDTH_M / 2);
  return {
    kind: 'line' as const,
    id,
    from: { x: side === 'left' ? 0 : HALF, y },
    to: { x: side === 'left' ? HALF : 1, y },
    color: COLORS.subArea,
    width: ly(SUB_AREA_WIDTH_M),
  };
}

/**
 * Officials' equipment on the table side.
 *
 * The field-of-play diagram shows the table, goal judges, shot clocks and ball
 * baskets as indicative positions with no dimensions attached, so these are
 * **schematic markers, not to-scale objects**. Their sizes and positions are a
 * presentation choice; nothing here should be measured off or cited as a rule.
 * They sit on the 1.00 m to 2.20 m deck band, between the clear water and the
 * referee platform.
 */
function venueBox(
  id: string,
  edge: 0 | 1,
  xCentreM: number,
  outFromM: number,
  widthM: number,
  depthM: number,
) {
  return {
    kind: 'rect' as const,
    id,
    at: {
      x: 0.5 + lx(xCentreM) - lx(widthM) / 2,
      // `at` is the low-y corner, which is the far edge of the band on the top
      // side and the near edge on the bottom one — so the same call places a box
      // in the same band whichever sideline it is on.
      y: outward(edge, edge === 0 ? outFromM + depthM : outFromM),
    },
    size: { w: lx(widthM), h: ly(depthM) },
    color: COLORS.venue,
    fill: COLORS.venueFill,
    width: LINE,
  };
}

/**
 * A team bench, on the bench side at its own team's end.
 *
 * `side` is the half the bench belongs to, which 18.2.7 ties to the goal line its
 * flying substitution segment runs from. The bench is inset from that goal line
 * so it reads as standing "in front of" it, and stands on the deck band between
 * the substitution water and the referee platform. Its size is a presentation
 * choice — see BENCH_LENGTH_M.
 */
function teamBench(id: string, side: 'left' | 'right') {
  const fromGoalLine = BENCH_INSET_M + BENCH_LENGTH_M / 2;
  const centreM = side === 'left' ? -LENGTH_M / 2 + fromGoalLine : LENGTH_M / 2 - fromGoalLine;
  return venueBox(id, FAR_EDGE, centreM, BENCH_OUT_M, BENCH_LENGTH_M, BENCH_DEPTH_M);
}

/** A ball basket, beyond a corner on the table side. Schematic, like the rest. */
function ballBasket(id: string, side: 'left' | 'right') {
  return {
    kind: 'circle' as const,
    id,
    at: {
      x: side === 'left' ? -lx(1.0) : 1 + lx(1.0),
      y: outward(TABLE_EDGE, 2.6),
    },
    radius: ly(0.45),
    color: COLORS.venue,
    fill: COLORS.venueFill,
    width: LINE,
  };
}

/** Labels a venue marking. `edge` must be the sideline the thing it names sits on. */
function venueLabel(id: string, edge: 0 | 1, x: number, outFromM: number, text: string) {
  return {
    kind: 'label' as const,
    id,
    at: { x, y: outward(edge, outFromM) },
    text,
    size: 0.024,
    color: COLORS.venueLabel,
    align: 'center' as const,
  };
}

function distanceLabel(id: string, x: number, text: string) {
  return {
    kind: 'label' as const,
    id,
    at: { x, y: ly(0.95) },
    text,
    size: 0.028,
    color: COLORS.label,
    align: 'center' as const,
  };
}

/* ------------------------------------------------------------------ */
/* Formations                                                          */
/* ------------------------------------------------------------------ */

const slot = (teamId: string, roleId: string, label: string, x: number, y: number): FormationSlot => ({
  teamId,
  roleId,
  label,
  at: { x, y },
});

/** Classic 6-on-6 set offence: centre forward on 2 m, five perimeter players. */
const setOffence = {
  id: 'set-offence-6v6',
  name: '6 v 6 set offence',
  slots: [
    // White attacks the right-hand goal.
    slot('home', 'goalkeeper', '1', 0.032, 0.5),
    slot('home', 'centre-forward', '5', 0.9, 0.5),
    slot('home', 'wing', '4', 0.915, 0.24),
    slot('home', 'wing', '6', 0.915, 0.76),
    slot('home', 'flat', '3', 0.775, 0.3),
    slot('home', 'flat', '7', 0.775, 0.7),
    slot('home', 'point', '2', 0.72, 0.5),
    // Blue defends it.
    slot('away', 'goalkeeper', '1', 0.968, 0.5),
    slot('away', 'centre-back', '5', 0.937, 0.5),
    slot('away', 'wing', '4', 0.946, 0.262),
    slot('away', 'wing', '6', 0.946, 0.738),
    slot('away', 'flat', '3', 0.816, 0.315),
    slot('away', 'flat', '7', 0.816, 0.685),
    slot('away', 'point', '2', 0.766, 0.5),
  ],
  props: [{ typeId: 'ball', at: { x: 0.702, y: 0.462 } }],
};

/**
 * The standard seven per team, in the order `+` fills a team and `−` empties
 * it (docs/rules/water-polo-positions.md §3). Spots and numbers are the default
 * 6 v 6's; the spot it gives a point goes to whichever of the center and the
 * center defender that formation leaves out.
 */
const standardLineup: readonly FormationSlot[] = [
  // White attacks the right-hand goal; its right hand faces down the screen.
  slot('home', 'goalkeeper', '1', 0.032, 0.5),
  slot('home', 'centre-forward', '5', 0.9, 0.5),
  slot('home', 'centre-back', '2', 0.72, 0.5),
  slot('home', 'wing', '6', 0.915, 0.76),
  slot('home', 'wing', '4', 0.915, 0.24),
  slot('home', 'flat', '7', 0.775, 0.7),
  slot('home', 'flat', '3', 0.775, 0.3),
  // Blue attacks the left-hand goal; its right hand faces up the screen.
  slot('away', 'goalkeeper', '1', 0.968, 0.5),
  slot('away', 'centre-forward', '2', 0.766, 0.5),
  slot('away', 'centre-back', '5', 0.937, 0.5),
  slot('away', 'wing', '4', 0.946, 0.262),
  slot('away', 'wing', '6', 0.946, 0.738),
  slot('away', 'flat', '3', 0.816, 0.315),
  slot('away', 'flat', '7', 0.816, 0.685),
];

/** Man-up: attackers in 4–2, five defenders in a 3–2 zone. */
const manUp = {
  id: 'man-up-4-2',
  name: '6 on 5 (4–2)',
  slots: [
    slot('home', 'goalkeeper', '1', 0.032, 0.5),
    slot('home', 'wing', '2', 0.917, 0.2),
    slot('home', 'flat', '3', 0.917, 0.4),
    slot('home', 'flat', '4', 0.917, 0.6),
    slot('home', 'wing', '5', 0.917, 0.8),
    slot('home', 'point', '6', 0.757, 0.35),
    slot('home', 'point', '7', 0.757, 0.65),
    slot('away', 'goalkeeper', '1', 0.968, 0.5),
    slot('away', 'centre-back', '3', 0.945, 0.3),
    slot('away', 'centre-back', '4', 0.945, 0.5),
    slot('away', 'centre-back', '5', 0.945, 0.7),
    slot('away', 'flat', '2', 0.836, 0.4),
    slot('away', 'flat', '6', 0.836, 0.6),
  ],
  props: [{ typeId: 'ball', at: { x: 0.742, y: 0.322 } }],
};

/** Empty pool, ball only — for freeform drawing and drills. */
const blank = {
  id: 'blank',
  name: 'Empty pool',
  slots: [],
  props: [{ typeId: 'ball', at: { x: 0.5, y: 0.5 } }],
};

/* ------------------------------------------------------------------ */

export const waterPoloPack: SportPack = {
  id: 'water-polo',
  name: 'Water polo',
  version: '1.0.0',

  surface: {
    dimensions: { length: LENGTH_M, width: WIDTH_M, unit: 'm' },
    aspectRatio: LENGTH_M / WIDTH_M,
    background: COLORS.water,
    markings: [
      // Field boundary (the goal lines are the short edges).
      {
        kind: 'rect',
        id: 'boundary',
        at: { x: 0, y: 0 },
        size: { w: 1, h: 1 },
        color: COLORS.white,
        width: LINE,
      },
      ...ropeSegments(0, 'rope-top'),
      ...ropeSegments(1, 'rope-bottom'),
      twoMetreBox('two-metre-box-left', 'left'),
      twoMetreBox('two-metre-box-right', 'right'),
      edgeMark('two-metre-mark-left-top', TWO_M, 'top', COLORS.red),
      edgeMark('two-metre-mark-left-bottom', TWO_M, 'bottom', COLORS.red),
      edgeMark('two-metre-mark-right-top', 1 - TWO_M, 'top', COLORS.red),
      edgeMark('two-metre-mark-right-bottom', 1 - TWO_M, 'bottom', COLORS.red),
      // The penalty throw point. The field-of-play diagram dimensions it 5.30,
      // measured from the pool wall — 0.30 m behind the goal line. It is 5 m.
      edgeMark('five-metre-mark-left-top', FIVE_M, 'top', COLORS.red),
      edgeMark('five-metre-mark-left-bottom', FIVE_M, 'bottom', COLORS.red),
      edgeMark('five-metre-mark-right-top', 1 - FIVE_M, 'top', COLORS.red),
      edgeMark('five-metre-mark-right-bottom', 1 - FIVE_M, 'bottom', COLORS.red),
      verticalLine('six-metre-left', SIX_M, COLORS.yellow),
      verticalLine('six-metre-right', 1 - SIX_M, COLORS.yellow),
      halfwayLine('halfway-top', 0),
      halfwayLine('halfway-bottom', 1),
      {
        kind: 'circle',
        id: 'centre-mark',
        at: { x: HALF, y: HALF },
        radius: CENTRE_MARK_RADIUS,
        color: COLORS.white,
        width: LINE,
      },
      goal('goal-left', 'left'),
      goal('goal-right', 'right'),
      reEntryArea('re-entry-left', 'left'),
      reEntryArea('re-entry-right', 'right'),
      distanceLabel('label-2m-left', TWO_M, '2 m'),
      distanceLabel('label-6m-left', SIX_M, '6 m'),
      distanceLabel('label-6m-right', 1 - SIX_M, '6 m'),
      distanceLabel('label-2m-right', 1 - TWO_M, '2 m'),
    ],

    surround: {
      left: SURROUND_END_M,
      right: SURROUND_END_M,
      top: SURROUND_LATERAL_M,
      bottom: SURROUND_LATERAL_M,
    },

    // Venue, not field of play: off by default, and the surround above is only
    // reserved when it is shown. Everything here is outside the water.
    venueMarkings: [
      ...refereePlatform(TABLE_EDGE, 'platform-top'),
      ...refereePlatform(FAR_EDGE, 'platform-bottom'),
      substitutionArea('substitution-area-left', 'left'),
      substitutionArea('substitution-area-right', 'right'),
      venueLabel('label-substitution-left', FAR_EDGE, 0.25, SUB_AREA_WIDTH_M / 2, 'Flying substitution'),
      venueLabel('label-substitution-right', FAR_EDGE, 0.75, SUB_AREA_WIDTH_M / 2, 'Flying substitution'),
      teamBench('team-bench-left', 'left'),
      teamBench('team-bench-right', 'right'),
      venueLabel('label-bench-left', FAR_EDGE, lx(BENCH_INSET_M + BENCH_LENGTH_M / 2), BENCH_OUT_M + BENCH_DEPTH_M / 2, 'Team bench'),
      venueLabel(
        'label-bench-right',
        FAR_EDGE,
        1 - lx(BENCH_INSET_M + BENCH_LENGTH_M / 2),
        BENCH_OUT_M + BENCH_DEPTH_M / 2,
        'Team bench',
      ),
      venueBox('officials-table', TABLE_EDGE, 0, 1.2, 6.0, 0.9),
      venueLabel('label-officials-table', TABLE_EDGE, 0.5, 1.65, "Officials' table"),
      venueBox('goal-judge-left', TABLE_EDGE, -LENGTH_M / 2, 1.1, 0.6, 0.6),
      venueBox('goal-judge-right', TABLE_EDGE, LENGTH_M / 2, 1.1, 0.6, 0.6),
      venueBox('shot-clock-left', TABLE_EDGE, -LENGTH_M / 2 - 1.1, 1.2, 1.2, 0.8),
      venueBox('shot-clock-right', TABLE_EDGE, LENGTH_M / 2 + 1.1, 1.2, 1.2, 0.8),
      ballBasket('ball-basket-left', 'left'),
      ballBasket('ball-basket-right', 'right'),
    ],
  },

  teams: [
    {
      id: 'home',
      name: 'Team White',
      color: '#f4f7fb',
      textColor: '#0a2440',
      outline: '#0a2440',
      attacking: 'right',
    },
    {
      id: 'away',
      name: 'Team Blue',
      color: '#1740b8',
      textColor: '#ffffff',
      outline: '#dbe6ff',
      attacking: 'left',
    },
  ],

  entityTypes: [
    { id: 'player', name: 'Player', shape: 'disc', radius: 0.028, draggable: true },
    { id: 'ball', name: 'Ball', shape: 'ball', radius: 0.016, draggable: true, color: '#ffd83d' },
  ],

  roles: [
    // Names and short forms are US nomenclature, from
    // docs/rules/water-polo-positions.md §1. The ids are stored in coaches'
    // boards and never change; only these display names follow the document.
    { id: 'goalkeeper', name: 'Goalie', short: 'G', color: '#e0323c', textColor: '#ffffff' },
    { id: 'centre-forward', name: 'Center', short: 'C' },
    { id: 'centre-back', name: 'Center defender', short: 'CD' },
    { id: 'point', name: 'Point', short: 'P' },
    { id: 'flat', name: 'Flat', short: 'F' },
    { id: 'wing', name: 'Wing', short: 'W' },
  ],

  formations: [setOffence, manUp, blank],
  defaultFormationId: setOffence.id,

  /**
   * Seven in the water: six field players plus a goalkeeper, who does not count
   * toward the six. Read literally as "six per team" both formations above would
   * be illegal the moment they load — see
   * docs/superpowers/specs/2026-08-30-roster-controls-design.md.
   */
  roster: {
    maxPerTeam: 6,
    exemptRoles: [{ roleId: 'goalkeeper', max: 1 }],
    playerTypeId: 'player',
    lineup: standardLineup,
  },

  /**
   * A player has the ball when it is within about 1.5 m — roughly one arm's
   * reach, which is 0.06 of a 25 m length — and no other player is nearer to
   * it. A swim by that player takes the ball along: that is a dribble.
   *
   * An editor tolerance rather than field geometry: it says when the board
   * should treat a player as carrying the ball, and no rule of water polo fixes
   * it. That is why it lives here and not in `docs/rules/water-polo-field.md`.
   */
  possession: {
    carriedTypeId: 'ball',
    radius: 0.06,
  },

  tools: {
    strokeColor: '#ffe25a',
    strokeWidth: 0.007,
    palette: ['#ffe25a', '#ff6b5e', '#5ee0a0', '#7fb8ff', '#ffffff', '#101820'],
  },

  terminology: {
    'surface.name': 'Pool',
    'surface.short': 'Pool',
    'entity.player': 'Player',
    'entity.ball': 'Ball',
    'tool.move': 'Swim',
    'tool.pass': 'Pass',
    'tool.shot': 'Shot',
    'tool.freehand': 'Freehand',
    'play.keyframe': 'Phase',
  },
};

export default waterPoloPack;
